import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import {
  Close,
  Download,
  TrendingDown,
  Edit,
  Delete,
  MyLocation,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import drawdownService from "../../../../../services/drawdownService";
import { triggerVirtualBore } from "../../../components/VirtualBore/triggerVirtualBore";
import DetailsTableRow from "./DetailsTableRow";
import { CACHE_TIMES, DATE_FORMATS } from "../constants/drawdownConstants";

const formatTimestamp = (timestamp) => {
  return new Date(timestamp).toLocaleString("en-US", DATE_FORMATS.FULL);
};

const ResultsDialog = ({ open, onClose, runNdx, onAutofill }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch run details
  const {
    data: runDetails,
    isLoading: detailsLoading,
    error: detailsError,
  } = useQuery(
    ["drawdown-run-details", runNdx],
    () =>
      drawdownService.getRunDetails(
        runNdx,
        getAccessTokenSilently,
        isAuthenticated
      ),
    {
      enabled: open && !!runNdx && isAuthenticated,
      staleTime: CACHE_TIMES.RUN_DETAILS,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch run results
  const {
    data: runResults = [],
    isLoading: resultsLoading,
    error: resultsError,
  } = useQuery(
    ["drawdown-run-results", runNdx],
    () =>
      drawdownService.getRunResults(
        runNdx,
        getAccessTokenSilently,
        isAuthenticated
      ),
    {
      enabled: open && !!runNdx && isAuthenticated,
      staleTime: CACHE_TIMES.RUN_DETAILS,
      refetchOnWindowFocus: false,
    }
  );

  const loading = detailsLoading || resultsLoading;
  const error = detailsError || resultsError;

  // Delete mutation
  const deleteMutation = useMutation(
    () =>
      drawdownService.deleteRun(
        runNdx,
        getAccessTokenSilently,
        isAuthenticated
      ),
    {
      onSuccess: () => {
        // Invalidate and refetch runs list
        void queryClient.invalidateQueries("drawdown-runs");
        onClose();
      },
    }
  );

  const handleDownload = async () => {
    try {
      await drawdownService.downloadReport(
        runNdx,
        getAccessTokenSilently,
        isAuthenticated
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    deleteMutation.mutate();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleAutofill = () => {
    onAutofill?.(runDetails);
  };

  const handleVirtualBore = () => {
    if (runDetails?.x && runDetails?.y) {
      triggerVirtualBore(runDetails.y, runDetails.x); // Note: API expects lat, lon (y, x)
    }
  };

  const renderRunDetails = () => (
    <Box sx={{ p: 2 }}>
      {runDetails && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Run Information
          </Typography>

          <Table size="small">
            <TableBody>
              <DetailsTableRow
                label="Proposed Well Name"
                value={runDetails.proposed_well_name}
              />
              <DetailsTableRow
                label="Aquifer"
                value={runDetails.source_aquifer}
              />
              <DetailsTableRow label="X" value={runDetails.x} />
              <DetailsTableRow label="Y" value={runDetails.y} />
              <DetailsTableRow
                label="Search (ft)"
                value={runDetails.search_radius_ft}
                format="number"
              />
              <DetailsTableRow
                label="Annual (af)"
                value={runDetails.annual_af_requested}
              />
              <DetailsTableRow
                label="Discharge (gpm)"
                value={runDetails.discharge_gpm}
              />
              <DetailsTableRow
                label="Transmissivity Used for Calculations"
                value={runDetails.modeled_transmissivity}
              />
              <DetailsTableRow
                label="Source of Transmissivity Value"
                value={runDetails.trans_source}
              />
              <DetailsTableRow
                label="Storativity Used for Calculations"
                value={runDetails.modeled_storativity}
              />
              <DetailsTableRow
                label="Source of Storativity Value"
                value={runDetails.stor_source}
              />
              <DetailsTableRow label="Notes" value={runDetails.run_notes} />
              <DetailsTableRow
                label="Run Time"
                value={formatTimestamp(runDetails.run_timestamp)}
              />
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );

  const renderRunResults = () => (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Impact Analysis Results
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Wells within search radius and predicted impact levels
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Well Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Distance (ft)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                1-Day Drawdown (ft)
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                30-Day Drawdown (ft)
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                1-Year Drawdown (ft)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runResults.map((result, index) => (
              <TableRow key={index}>
                <TableCell>{result.well_name}</TableCell>
                <TableCell>
                  {Math.round(result.distance_ft).toLocaleString()}
                </TableCell>
                <TableCell>
                  {result.drawdown_daily?.toFixed(2) || "0.00"}
                </TableCell>
                <TableCell>
                  {result.drawdown_monthly?.toFixed(2) || "0.00"}
                </TableCell>
                <TableCell>
                  {result.drawdown_annual?.toFixed(2) || "0.00"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {runResults.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No wells found within the specified search radius.
        </Alert>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingDown color="primary" />
          <Typography variant="h6">Drawdown Analysis Results</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              {error?.message || "Failed to load run data"}
            </Alert>
          </Box>
        )}

        {!loading && !error && (
          <Box>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Run Details" />
              <Tab label={`Results (${runResults.length})`} />
            </Tabs>

            {activeTab === 0 && renderRunDetails()}
            {activeTab === 1 && renderRunResults()}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          startIcon={<Delete />}
          onClick={handleDelete}
          disabled={loading || error || deleteMutation.isLoading}
          color="error"
          size="small"
          sx={{
            "& .MuiButton-startIcon": {
              mr: { xs: 0, sm: 1 },
            },
            minWidth: { xs: "auto", sm: "64px" },
          }}
        >
          {deleteMutation.isLoading ? "Deleting..." : "Delete"}
        </Button>
        <Button
          startIcon={<Edit />}
          onClick={handleAutofill}
          disabled={loading || error}
          color="primary"
          size="small"
        >
          Autofill
        </Button>
        <Button
          startIcon={<MyLocation />}
          onClick={handleVirtualBore}
          disabled={loading || error}
          color="secondary"
          size="small"
        >
          Virtual Bore
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={<Download />}
          onClick={handleDownload}
          disabled={loading || error}
          variant="outlined"
          size="small"
        >
          <Box sx={{ display: { xs: "none", sm: "inline" } }}>
            Download Report
          </Box>
          <Box sx={{ display: { xs: "inline", sm: "none" } }}>Download</Box>
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          size="small"
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        >
          Close
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this drawdown analysis run?
            {runDetails?.proposed_well_name && (
              <>
                <br />
                <strong>Run: {runDetails.proposed_well_name}</strong>
              </>
            )}
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            Delete Run
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ResultsDialog;
